import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  RefreshCw,
  Upload,
} from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'

import { formatMoney, MONEY_SCALE, parseHumanAmount } from '@aeris/money'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Select,
  Spinner,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchProductCategories } from '../lib/api/product-categories.ts'
import {
  ApiError,
  applyProductRecode,
  previewProductRecode,
  type RecodeCategoryRule,
  type RecodePreview,
  type RecodeResult,
  type RecodeRowSample,
} from '../lib/api.ts'
import { categoryLabel } from '../lib/category-label.ts'

/**
 * Subir a planilha do sistema antigo — F165, e a criação em F166.
 *
 * ## Por que é uma tela separada da importação
 *
 * A importação de catálogo casa pelo CÓDIGO do outro sistema, e esse código se
 * move: o mesmo `4` foi um leitor de código de barras em abril e uma bateria
 * em agosto. Aqui quem casa é o NOME, porque é justamente o código que vai
 * mudar. Dois caminhos diferentes, com consequências diferentes e nenhuma
 * reversível — por isso dois botões, e não um.
 *
 * ## O que ela faz
 *
 * Produto que já existe: passa a usar o código, a barra, o custo e o preço da
 * planilha. Saldo, marca, unidade, categoria e nome ficam como estão.
 * Produto que não existe: nasce, com o que a planilha sabe e saldo zero.
 *
 * ## O que a prévia tem de mostrar
 *
 * Os quatro números, os problemas agrupados por causa — nunca 380 linhas — e
 * uma amostra do **de → para**. Quem vai clicar precisa ver o código velho e o
 * novo lado a lado antes, não depois. E precisa ver, antes de criar quatro mil
 * produtos, uma amostra do que vai nascer.
 */

const PROBLEM_LABELS: Record<string, string> = {
  ambiguous: 'Dois produtos com este mesmo nome',
  'sku-taken': 'O código já é de outro produto',
  'gtin-taken': 'O código de barras já é de outro produto',
  'duplicate-description': 'Nome repetido na própria planilha',
  'missing-description': 'Linha sem descrição',
  'missing-code': 'Linha sem código',
  'parked-code': 'Código guardado no sistema antigo (REUTILIZAR)',
  'unreadable-price': 'Preço ou custo que não pôde ser lido',
  'repaired-price': 'Preço corrigido — o sistema antigo gravou com erro',
}

const money = (wire: string | null): string => {
  if (wire === null) return '—'
  const amount = parseHumanAmount(wire.replace('.', ','), MONEY_SCALE)
  return amount === null ? wire : formatMoney(amount)
}

/** `de → para`, ou um traço quando o campo não muda. */
const Change = ({
  change,
  as = (value) => value ?? '—',
}: {
  change: { from: string | null; to: string | null } | null
  as?: (value: string | null) => string
}): ReactNode =>
  change === null ? (
    <span className="text-(--text-muted)">—</span>
  ) : (
    <span className="flex items-center gap-1">
      <span className="text-(--text-muted) line-through">
        {as(change.from)}
      </span>
      <ArrowRight aria-hidden="true" className="h-3 w-3 text-(--text-muted)" />
      <span className="font-(--weight-medium) text-(--text-primary)">
        {as(change.to)}
      </span>
    </span>
  )

/** A amostra do de → para. A mesma tabela serve ao que muda e ao que nasce. */
const SampleTable = ({
  rows,
}: {
  rows: readonly RecodeRowSample[]
}): ReactNode => (
  <TableContainer>
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Produto</TableHeaderCell>
          <TableHeaderCell>Código</TableHeaderCell>
          <TableHeaderCell>Código de barras</TableHeaderCell>
          <TableHeaderCell>Custo</TableHeaderCell>
          <TableHeaderCell>Preço</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.line}>
            <TableCell>{row.currentName ?? row.description}</TableCell>
            <TableCell>
              <Change change={row.sku} />
            </TableCell>
            <TableCell>
              <Change change={row.gtin} />
            </TableCell>
            <TableCell>
              <Change change={row.cost} as={money} />
            </TableCell>
            <TableCell>
              <Change change={row.price} as={money} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

export const RecodePanel = ({ canWrite }: { canWrite: boolean }): ReactNode => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<RecodePreview | null>(null)
  const [result, setResult] = useState<RecodeResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  /*
   * A categoria das peças NOVAS, pelo começo do nome.
   *
   * Começa com os dois prefixos desta loja — `f.` é tela, `bat.` é bateria —,
   * ambos sem categoria escolhida: enquanto ninguém escolher, ninguém
   * classifica nada. É o mesmo par prefixo → categoria do botão do Estoque
   * (F163), de propósito: duas regras diferentes para a mesma pergunta
   * acabariam discordando.
   */
  const [rules, setRules] = useState<RecodeCategoryRule[]>([
    { prefix: 'f.', categoryId: '' },
    { prefix: 'bat.', categoryId: '' },
  ])

  const categories = useQuery({
    queryKey: ['product-categories', 'active'],
    queryFn: async ({ signal }) =>
      await fetchProductCategories('active', signal),
  })

  const usableRules = rules.filter(
    (rule) => rule.prefix.trim() !== '' && rule.categoryId !== ''
  )

  const read = useMutation({
    mutationFn: previewProductRecode,
    onSuccess: (data) => {
      setPreview(data)
      setResult(null)
      setError(null)
    },
    onError: (failure: unknown) => {
      setPreview(null)
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'Não foi possível ler a planilha.'
      )
    },
  })

  const apply = useMutation({
    mutationFn: async (chosen: File) =>
      await applyProductRecode(chosen, usableRules),
    onSuccess: (data) => {
      setResult(data)
      setError(null)
    },
    onError: (failure: unknown) => {
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'A atualização não pôde ser concluída.'
      )
    },
  })

  const choose = (chosen: File | null): void => {
    if (chosen === null) return
    setFile(chosen)
    setResult(null)
    read.mutate(chosen)
  }

  const restart = (): void => {
    setFile(null)
    setPreview(null)
    setResult(null)
    setError(null)
    if (inputRef.current !== null) inputRef.current.value = ''
  }

  return (
    <div className="flex flex-col gap-6">
      {result === null && (
        <section className="flex flex-col items-center gap-3 rounded-(--radius-md) border-2 border-dashed border-(--border-subtle) p-8 text-center">
          <FileSpreadsheet
            aria-hidden="true"
            className="h-8 w-8 text-(--text-muted)"
          />
          <div>
            <p className="text-(length:--text-md) font-(--weight-medium) text-(--text-primary)">
              {file === null
                ? 'Escolha a planilha do sistema antigo'
                : `${file.name} · ${(file.size / 1024).toFixed(0)} KB`}
            </p>
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              Arquivo .xlsx com Código, Cód.Barras, Descrição, Preço Custo e
              Preço Venda. Os produtos são encontrados pelo nome.
            </p>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            className="sr-only"
            id="recode-file"
            onChange={(event) => {
              choose(event.target.files?.item(0) ?? null)
            }}
          />
          <Button
            type="button"
            variant="secondary"
            leadingIcon={<Upload aria-hidden="true" className="h-4 w-4" />}
            onClick={() => inputRef.current?.click()}
          >
            Escolher arquivo
          </Button>
        </section>
      )}

      {read.isPending && (
        <div className="flex items-center justify-center gap-3 py-8">
          <Spinner />
          <span className="text-(length:--text-sm) text-(--text-secondary)">
            Lendo a planilha…
          </span>
        </div>
      )}

      {/*
       * O erro abre num modal: o botão de atualizar fica no pé de uma prévia
       * longa, e uma faixa no topo da página passava sem ninguém ver.
       */}
      <Dialog
        open={error !== null}
        onOpenChange={(open) => {
          if (!open) setError(null)
        }}
      >
        <DialogContent title="Não deu certo" description={error ?? ''}>
          <DialogFooter>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                setError(null)
              }}
            >
              Entendi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {preview !== null && result === null && (
        <>
          <section className="flex flex-col gap-3">
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              {preview.rowCount.toLocaleString('pt-BR')} linhas em{' '}
              {preview.fileName}
            </p>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="Vão ser criados"
                value={preview.counts.create.toLocaleString('pt-BR')}
                hint="não existem aqui ainda"
              />
              <StatTile
                label="Vão mudar"
                value={preview.counts.update.toLocaleString('pt-BR')}
                hint="código, barras, custo ou preço"
              />
              <StatTile
                label="Já estão certos"
                value={preview.counts.unchanged.toLocaleString('pt-BR')}
                hint="nada a fazer nestes"
              />
              <StatTile
                label="Ficam de fora"
                value={preview.counts.skip.toLocaleString('pt-BR')}
                hint="veja os motivos abaixo"
              />
            </div>

            <p
              role="note"
              className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4 text-(length:--text-sm) text-(--text-secondary)"
            >
              Nos produtos que já existem, o estoque, a marca, a unidade, a
              categoria e o nome não são tocados. Os que não existem nascem com
              saldo zero, sem marca, e já podem ser vendidos.
              {preview.counts.untouched > 0 && (
                <>
                  {' '}
                  Os {preview.counts.untouched.toLocaleString('pt-BR')} produtos
                  do sistema que não aparecem nesta planilha ficam como estão.
                </>
              )}
            </p>
          </section>

          {preview.problems.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
                O que ficou de fora, e por quê
              </h2>
              {preview.problems.map((problem) => (
                <div
                  key={problem.problem}
                  className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-3"
                >
                  <p className="text-(length:--text-sm) text-(--text-primary)">
                    <strong>{problem.count.toLocaleString('pt-BR')}</strong>{' '}
                    {PROBLEM_LABELS[problem.problem] ?? problem.problem}
                  </p>
                  <p className="text-(length:--text-xs) text-(--text-muted)">
                    {problem.sample.join(' · ')}
                  </p>
                </div>
              ))}
            </section>
          )}

          {preview.counts.create > 0 && (
            <section className="flex flex-col gap-3">
              <h2 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
                A categoria dos produtos novos
              </h2>
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                Pelo começo do nome. O que não casar com nenhuma regra entra sem
                categoria — dá para classificar depois, na página de Estoque.
              </p>

              {rules.map((rule, index) => (
                <div
                  key={String(index)}
                  className="flex flex-wrap items-end gap-2"
                >
                  <Field label="Começa com">
                    {(field) => (
                      <Input
                        id={field.id}
                        value={rule.prefix}
                        className="w-32"
                        onChange={(event) => {
                          const { value } = event.target
                          setRules((current) =>
                            current.map((each, position) =>
                              position === index
                                ? { ...each, prefix: value }
                                : each
                            )
                          )
                        }}
                      />
                    )}
                  </Field>
                  <Field label="Categoria">
                    {(field) => (
                      <Select
                        id={field.id}
                        value={rule.categoryId}
                        className="w-56"
                        onChange={(event) => {
                          const { value } = event.target
                          setRules((current) =>
                            current.map((each, position) =>
                              position === index
                                ? { ...each, categoryId: value }
                                : each
                            )
                          )
                        }}
                      >
                        <option value="">Sem categoria</option>
                        {(categories.data ?? []).map((category) => (
                          <option key={category.id} value={category.id}>
                            {categoryLabel(category)}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRules((current) =>
                        current.filter((_, position) => position !== index)
                      )
                    }}
                  >
                    Tirar
                  </Button>
                </div>
              ))}

              <div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setRules((current) => [
                      ...current,
                      { prefix: '', categoryId: '' },
                    ])
                  }}
                >
                  Mais uma regra
                </Button>
              </div>
            </section>
          )}

          {preview.creating.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
                Produtos que vão nascer
              </h2>
              <SampleTable rows={preview.creating} />
            </section>
          )}

          {preview.sample.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
                Produtos que vão mudar
              </h2>
              <SampleTable rows={preview.sample} />
            </section>
          )}

          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={restart}>
              Trocar arquivo
            </Button>
            <Button
              type="button"
              disabled={
                !canWrite ||
                apply.isPending ||
                file === null ||
                preview.counts.update + preview.counts.create === 0
              }
              leadingIcon={
                apply.isPending ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                )
              }
              onClick={() => {
                if (file !== null) apply.mutate(file)
              }}
            >
              {apply.isPending
                ? 'Gravando…'
                : preview.counts.create > 0
                  ? 'Criar e atualizar os produtos'
                  : 'Atualizar os produtos'}
            </Button>
          </div>
        </>
      )}

      {result !== null && (
        <section
          aria-label="Atualização concluída"
          className="flex flex-col gap-4 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-6"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2
              aria-hidden="true"
              className="h-5 w-5 text-(--success)"
            />
            <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
              Planilha aplicada
            </h2>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatTile
              label="Criados"
              value={result.created.toLocaleString('pt-BR')}
            />
            <StatTile
              label="Códigos"
              value={result.recoded.toLocaleString('pt-BR')}
            />
            <StatTile
              label="Códigos de barras"
              value={result.barcoded.toLocaleString('pt-BR')}
            />
            <StatTile
              label="Custos"
              value={result.costed.toLocaleString('pt-BR')}
            />
            <StatTile
              label="Preços"
              value={result.priced.toLocaleString('pt-BR')}
            />
          </div>

          {result.skipped > 0 && (
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              {result.skipped.toLocaleString('pt-BR')} linhas ficaram de fora —
              os motivos estavam na prévia. O que entrou já está valendo.
            </p>
          )}

          <Button
            type="button"
            variant="secondary"
            leadingIcon={<RefreshCw aria-hidden="true" className="h-4 w-4" />}
            onClick={restart}
          >
            Enviar outra planilha
          </Button>
        </section>
      )}
    </div>
  )
}
